"use client";

import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog";
import * as React from "react";

import { buttonVariants } from "@/components/ui/button";
import { renderAsChild } from "@/components/ui/render-as-child";
import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

const AlertDialog = AlertDialogPrimitive.Root;

type CSSPropertiesWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

const AlertDialogTrigger = ({
  asChild,
  children,
  nativeButton = false,
  render,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger> & {
  asChild?: boolean;
}) => (
  <AlertDialogPrimitive.Trigger
    nativeButton={nativeButton}
    render={render ?? renderAsChild(asChild, children)}
    {...props}
  >
    {asChild ? null : children}
  </AlertDialogPrimitive.Trigger>
);
AlertDialogTrigger.displayName = "AlertDialogTrigger";

const AlertDialogPortal = (
  props: React.ComponentProps<typeof AlertDialogPrimitive.Portal>,
) => <AlertDialogPrimitive.Portal {...props} />;

const AlertDialogOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Backdrop>) => (
  <AlertDialogPrimitive.Backdrop
    className={(state) =>
      cn(
        `
          pointer-events-auto fixed inset-0 z-100 bg-black/80
          transition-opacity duration-200 data-ending-style:opacity-0
          data-starting-style:opacity-0
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
AlertDialogOverlay.displayName = "AlertDialogOverlay";

const AlertDialogContent = ({
  className,
  style,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Popup>) => {
  const keyboardAwareStyle: CSSPropertiesWithVars = {
    "--overlay-keyboard-inset-bottom":
      "var(--visual-keyboard-inset-bottom, 0px)",
    "--overlay-keyboard-inset-bottom-half":
      "var(--visual-keyboard-inset-bottom-half, 0px)",
    "--overlay-keyboard-offset-top": "var(--visual-keyboard-offset-top, 0px)",
    "--overlay-keyboard-offset-top-half":
      "var(--visual-keyboard-offset-top-half, 0px)",
    "--overlay-keyboard-available-height":
      "calc(100dvh - var(--overlay-keyboard-inset-bottom) - var(--overlay-keyboard-offset-top))",
    "--overlay-content-max-height":
      "calc(var(--overlay-keyboard-available-height) - 24px)",
    "--alert-dialog-content-center-y":
      "calc(50% + var(--overlay-keyboard-offset-top-half) - var(--overlay-keyboard-inset-bottom-half))",
    ...style,
  };

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Popup
        className={(state) =>
          cn(
            `
              pointer-events-auto fixed top-(--alert-dialog-content-center-y)
              left-[50%] z-100 grid max-h-(--overlay-content-max-height)
              w-full max-w-[90vw] -translate-1/2 gap-4
              border bg-background p-6 shadow-lg transition duration-200 ease-out
              data-ending-style:scale-95 data-ending-style:opacity-0
              data-starting-style:scale-95 data-starting-style:opacity-0
              sm:rounded-lg md:max-w-lg
            `,
            resolveClassName(className, state),
          )
        }
        style={keyboardAwareStyle}
        {...props}
      />
    </AlertDialogPortal>
  );
};
AlertDialogContent.displayName = "AlertDialogContent";

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `
        flex flex-col space-y-2 text-center
        sm:text-left
      `,
      className,
    )}
    {...props}
  />
);
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `
        flex flex-col-reverse gap-2 justify-self-start
        sm:flex-row-reverse
      `,
      className,
    )}
    {...props}
  />
);
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={(state) =>
      cn(
        "text-lg font-semibold text-muted-foreground",
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
AlertDialogTitle.displayName = "AlertDialogTitle";

const AlertDialogDescription = ({
  asChild,
  children,
  ref,
  className,
  render,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description> & {
  asChild?: boolean;
}) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={(state) =>
      cn("text-sm/6 text-muted", resolveClassName(className, state))
    }
    render={render ?? renderAsChild(asChild, children)}
    {...props}
  >
    {asChild ? null : children}
  </AlertDialogPrimitive.Description>
);
AlertDialogDescription.displayName = "AlertDialogDescription";

const AlertDialogAction = ({
  asChild,
  children,
  className,
  nativeButton = true,
  render,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Close> & {
  asChild?: boolean;
}) => (
  <AlertDialogPrimitive.Close
    nativeButton={nativeButton}
    render={
      render ??
      renderAsChild(asChild, children) ?? (
        <button
          className={cn(
            buttonVariants(),
            "align-start items-start justify-start",
            className,
          )}
        />
      )
    }
    {...props}
  >
    {render || asChild ? null : children}
  </AlertDialogPrimitive.Close>
);
AlertDialogAction.displayName = "AlertDialogAction";

const AlertDialogCancel = ({
  asChild,
  children,
  className,
  nativeButton = true,
  render,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Close> & {
  asChild?: boolean;
}) => (
  <AlertDialogPrimitive.Close
    nativeButton={nativeButton}
    render={
      render ??
      renderAsChild(asChild, children) ?? (
        <button
          className={cn(
            buttonVariants({ variant: "outline" }),
            `
              mt-2
              sm:mt-0
            `,
            className,
          )}
        />
      )
    }
    {...props}
  >
    {render || asChild ? null : children}
  </AlertDialogPrimitive.Close>
);
AlertDialogCancel.displayName = "AlertDialogCancel";

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogPrimitive,
  AlertDialogTitle,
  AlertDialogTrigger,
};
